// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * The things the user leaves with.
 *
 * A search result on a screen is not the product. The insurer wants a file, the
 * solicitor wants a file, and the person who spent four minutes scanning a
 * dashcam card should not have to do it again next week. So: the clips, a CSV
 * of what was found, a WebVTT chapter track that turns the queries into
 * permanent marks on the file, and the index itself so the video stays
 * searchable without re-embedding a single frame.
 */

import { timecode } from './sampling.ts';
import { COMPATIBLE_VISION_DTYPES } from '../model/config.mjs';
import { EMBED_DIM } from './score.ts';
import type { Moment, VideoFacts } from '../types.ts';

export const INDEX_VERSION = 1;

export interface QueryResult {
  readonly query: string;
  readonly moments: readonly Moment[];
}

/**
 * One row per moment, per query.
 *
 * RFC 4180 quoting, because a query is free text and people type commas,
 * quotes and newlines into search boxes constantly.
 */
export function cuepointsCsv(results: readonly QueryResult[], video: VideoFacts): string {
  const rows: string[][] = [
    ['file', 'query', 'rank', 'start_seconds', 'end_seconds', 'start_timecode', 'end_timecode', 'peak_seconds', 'peak_score'],
  ];
  for (const r of results) {
    r.moments.forEach((m, i) => {
      rows.push([
        video.name,
        r.query,
        String(i + 1),
        m.start.toFixed(3),
        m.end.toFixed(3),
        timecode(m.start),
        timecode(m.end),
        m.peakTime.toFixed(3),
        m.peakScore.toFixed(4),
      ]);
    });
  }
  return rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * A WebVTT chapter track.
 *
 * Chosen over a bespoke format because VLC, Plex, Jellyfin and YouTube all read
 * it natively — drop this next to the video and the moments you searched for
 * become chapter marks you can click, in software the user already has.
 *
 * Cues must be in ascending time order and must not overlap, or players
 * silently drop the later ones; both are enforced here rather than assumed of
 * the caller.
 */
export function chaptersVtt(results: readonly QueryResult[]): string {
  const cues: { start: number; end: number; label: string }[] = [];
  for (const r of results) {
    for (const m of r.moments) cues.push({ start: m.start, end: m.end, label: r.query });
  }
  cues.sort((a, b) => a.start - b.start || a.end - b.end);

  const lines = ['WEBVTT', '', 'NOTE Chapter marks generated by cuepoint — cuepoint.benrichardson.dev', ''];
  let n = 0;
  let previousEnd = -Infinity;
  for (const cue of cues) {
    const start = Math.max(cue.start, previousEnd);
    if (!(cue.end > start)) continue;
    n += 1;
    lines.push(String(n));
    lines.push(`${timecode(start)} --> ${timecode(cue.end)}`);
    // A cue payload is line-based, so an embedded newline would end the cue and
    // turn the rest of the query into a malformed block.
    lines.push(cue.label.replace(/[\r\n]+/g, ' ').trim() || 'moment');
    lines.push('');
    previousEnd = cue.end;
  }
  return lines.join('\n');
}

export interface IndexManifest {
  readonly format: 'cuepoint-index';
  readonly version: number;
  readonly created: string;
  readonly video: { name: string; bytes: number; duration: number; width: number; height: number };
  readonly model: { id: string; revision: string; visionDtype: string; dim: number };
  readonly rate: number;
  readonly times: number[];
}

export function buildManifest(
  video: VideoFacts,
  model: { id: string; revision: string; visionDtype: string },
  rate: number,
  times: readonly number[],
  now: string,
): IndexManifest {
  return {
    format: 'cuepoint-index',
    version: INDEX_VERSION,
    created: now,
    video: {
      name: video.name,
      bytes: video.bytes,
      duration: video.duration,
      width: video.width,
      height: video.height,
    },
    model: { id: model.id, revision: model.revision, visionDtype: model.visionDtype, dim: EMBED_DIM },
    rate,
    times: times.map((t) => Math.round(t * 1000) / 1000),
  };
}

/**
 * Validate a manifest a user has handed back to us.
 *
 * The check that matters is the model identity. Embeddings from a different
 * model, or a different quantisation of the same model, are not wrong-looking —
 * they are numbers of the right shape in the right range that mean something
 * else entirely, and searching them returns confident nonsense. This run
 * measured exactly that failure: the int8 export of this model's vision tower
 * agrees with fp32 on zero of eight queries. So a mismatched index is refused,
 * with the reason named.
 */
export function validateManifest(
  value: unknown,
  expect: { id: string; revision: string; visionDtype: string },
  vectorBytes: number,
): { ok: true; manifest: IndexManifest } | { ok: false; reason: string } {
  const m = value as Partial<IndexManifest> | null;
  if (!m || typeof m !== 'object') return { ok: false, reason: 'That file is not a cuepoint index.' };
  if (m.format !== 'cuepoint-index') return { ok: false, reason: 'That file is not a cuepoint index.' };
  if (m.version !== INDEX_VERSION) {
    return { ok: false, reason: `That index was written by a different version of cuepoint (v${String(m.version)}).` };
  }
  if (!Array.isArray(m.times) || m.times.length === 0) {
    return { ok: false, reason: 'That index lists no frames.' };
  }
  const model = m.model;
  // The dtype is checked against a compatibility SET, not for equality. fp16
  // and fp32 rank identically (measured 8/8), so an index built on a laptop
  // with a GPU must still open on a phone without one — refusing it would be
  // conservatism the user pays for with a re-scan. int8 is not in the set,
  // because it agrees with fp32 on none of eight queries.
  const compatible = model ? COMPATIBLE_VISION_DTYPES.includes(model.visionDtype) : false;
  if (!model || model.id !== expect.id || model.revision !== expect.revision || !compatible) {
    return {
      ok: false,
      reason:
        'That index was built with a different model, so its numbers do not mean the same thing as this one’s. Scan the video again.',
    };
  }
  if (model.dim !== EMBED_DIM) {
    return { ok: false, reason: `That index holds ${String(model.dim)}-dimension vectors; this build expects ${EMBED_DIM}.` };
  }
  const expected = m.times.length * EMBED_DIM * 4;
  if (vectorBytes !== expected) {
    return {
      ok: false,
      reason: `The index lists ${m.times.length} frames but its vector file holds ${String(Math.floor(vectorBytes / (EMBED_DIM * 4)))}. The two files do not belong together.`,
    };
  }
  return { ok: true, manifest: m as IndexManifest };
}

/** A filename that survives every filesystem the user might drop it on. */
export function safeName(base: string, suffix: string): string {
  const stem = base.replace(/\.[^./\\]+$/, '').replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
  return `${stem || 'video'}${suffix}`;
}

export function clipName(base: string, n: number): string {
  return safeName(base, `-clip-${String(n).padStart(3, '0')}.mp4`);
}
